import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi, type CleaningTeamResponse } from '@/shared/api';
import { useOnline } from '@/shared/hooks/useOnline';
import { teamFailure } from './teamFailure';
import { cleaningTeamKeys } from './teamKeys';

type CreateBody = Parameters<ReturnType<typeof useApi>['cleaningTeams']['create']>[0];

/**
 * Team anlegen oder speichern. Ohne Netz geht nichts hinaus; nach jedem Versuch lädt die Liste
 * neu, damit ein offener oder überholter Stand nicht im Dialog stehen bleibt.
 */
export function useTeamSave(editing: CleaningTeamResponse | null, close: () => void) {
  const api = useApi();
  const queryClient = useQueryClient();
  const online = useOnline();
  const onError = async (error: unknown) => {
    const failure = await teamFailure(error);
    toast.error(failure.text);
    // Offen oder überholt: der Dialog hält einen alten Stand; neu öffnen zeigt den aktuellen.
    if (failure.unknown || failure.stale) close();
  };
  const saved = (label: string) => {
    toast.success(label);
    close();
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: cleaningTeamKeys.all });
  const create = useMutation({
    mutationFn: api.cleaningTeams.create,
    networkMode: 'always',
    onSuccess: () => saved('Team angelegt'),
    onError,
    onSettled: refresh,
  });
  const update = useMutation({
    networkMode: 'always',
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof api.cleaningTeams.update>[1];
    }) => api.cleaningTeams.update(id, body),
    onSuccess: () => saved('Team gespeichert'),
    onError,
    onSettled: refresh,
  });
  const submit = (values: CreateBody & { isActive: boolean }) => {
    if (!online) {
      toast.error('Keine Verbindung – nichts wurde gespeichert.');
      return;
    }
    if (editing) {
      update.mutate({
        id: editing.id,
        body: { ...values, concurrencyStamp: editing.concurrencyStamp },
      });
    } else {
      create.mutate({
        name: values.name,
        companyId: values.companyId,
        members: values.members,
        propertyGroupIds: values.propertyGroupIds,
      });
    }
  };
  return { submit, saving: create.isPending || update.isPending, online };
}
